import { create } from 'zustand';
import { api } from '@/lib/api';
import type { ApiResponse } from '@/types';

interface TimerState {
  activeActivityId: string | null;
  activeActivityTitle: string | null;
  startTime: number | null;
  elapsed: number;
  intervalId: ReturnType<typeof setInterval> | null;

  startTimer: (activityId: string, activityTitle: string) => void;
  stopTimer: () => Promise<void>;
  resetTimer: () => void;
  tick: () => void;
}

export const useTimerStore = create<TimerState>((set, get) => ({
  activeActivityId: null,
  activeActivityTitle: null,
  startTime: null,
  elapsed: 0,
  intervalId: null,

  startTimer: (activityId: string, activityTitle: string) => {
    const state = get();
    if (state.intervalId) clearInterval(state.intervalId);

    const now = Date.now();
    const id = setInterval(() => get().tick(), 1000);

    set({
      activeActivityId: activityId,
      activeActivityTitle: activityTitle,
      startTime: now,
      elapsed: 0,
      intervalId: id,
    });
  },

  stopTimer: async () => {
    const state = get();
    if (state.intervalId) clearInterval(state.intervalId);

    if (state.activeActivityId && state.startTime) {
      const hours = Math.round((state.elapsed / 3600) * 100) / 100;
      if (hours >= 0.01) {
        try {
          await api.post<ApiResponse>('/time-entries', {
            activity_id: state.activeActivityId,
            hours: Math.max(hours, 0.01),
            description: 'Timer-Erfassung',
          });
        } catch (err) {
          console.error('Failed to save time entry:', err);
        }
      }
    }

    set({
      activeActivityId: null,
      activeActivityTitle: null,
      startTime: null,
      elapsed: 0,
      intervalId: null,
    });
  },

  resetTimer: () => {
    const state = get();
    if (state.intervalId) clearInterval(state.intervalId);
    set({
      activeActivityId: null,
      activeActivityTitle: null,
      startTime: null,
      elapsed: 0,
      intervalId: null,
    });
  },

  tick: () => {
    const state = get();
    if (state.startTime) {
      set({ elapsed: Math.floor((Date.now() - state.startTime) / 1000) });
    }
  },
}));
